import React from 'react';
import { View, StyleSheet, Text } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesome } from '@expo/vector-icons';
import Style from '../../../../../../constants/Style';
import Colors from '../../../../../../constants/Colors';
import { updateBuildingId } from '../../../../../../redux/selectedBuildingSlice';
import { AppScreen } from '../../../../../../navigation/LinkingConfiguration';
import { UserPermission } from '../../../../../../constants/Types';
import { Space } from '../../../../../../components/Map/SetFloorsModal/FloorItem';
import PressableStyled from '../../../../../../components/PressableStyled';
import EditIcon24 from '../../../../../../components/24pxIcons/EditIcon24';
import LocationIcon from '../../../../../../components/30pxIcons/LocationIcon';
import Button from '../../../../../../components/Button';

interface SpaceTitleItemProps extends Space {
  edit: () => void,
  clear: () => void,
}

const SpaceTitleItem = ({
  Name, assetCount, sensorCount, meterCount, floorName: floorId, areaName, buildingId, edit,
  LocationId: spaceId, ParentId: areaId, clear,
}: SpaceTitleItemProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const happyEnabled: boolean = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const canUpdateLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_UPDATE);
  const navigation = useNavigation();
  const dispatch = useDispatch();

  const navigateToMap = (params?) => {
    dispatch(updateBuildingId(buildingId));
    navigation.navigate(AppScreen.MAP as any, {
      floorId, areaId, spaceId, area: areaName, space: Name, ...params,
    });
  };

  const navigateToAssets: () => void = () => navigateToMap({ assetsView: true });

  const navigateToSensors: () => void = () => navigateToMap({ sensorsView: true });

  return <View style={styles.container}>
    <View style={styles.header}>
      <PressableStyled
        onPress={clear}
        style={styles.icon}
      >
        <FontAwesome
          name='angle-left'
          size={24}
          color={Colors.light.hereworksBlue500}
        />
      </PressableStyled>
      <Text style={styles.name}>{Name}</Text>
      {canUpdateLocations && <PressableStyled
        onPress={edit}
        style={styles.icon}
      >
        <EditIcon24 />
      </PressableStyled>}
      <PressableStyled
        onPress={navigateToMap}
        style={styles.icon}
      >
        <LocationIcon color={Colors.light.hereworksBlue500} />
      </PressableStyled>
    </View>
    <View style={Style.row}>
      <View style={styles.mainSection}>
        <Text style={styles.mainSectionTitle}>{'Assets'}</Text>
        <View style={Style.row}>
          <Text style={styles.mainSectionCount}>{assetCount ?? 0}</Text>
          <Button
            title='Go to map'
            style={styles.mainSectionButton}
            onPress={navigateToAssets}
          />
        </View>
      </View>
      {happyEnabled && <View style={styles.mainSection}>
        <Text style={styles.mainSectionTitle}>{'Sensors & Meters'}</Text>
        <View style={Style.row}>
          <Text style={styles.mainSectionCount}>{(sensorCount ?? 0) + (meterCount ?? 0)}</Text>
          <Button
            title='Go to map'
            style={styles.mainSectionButton}
            onPress={navigateToSensors}
          />
        </View>
      </View>}
    </View>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 40,
    marginHorizontal: 8,
  },
  name: {
    fontSize: 18,
    lineHeight: 28,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginHorizontal: 16,
  },
  icon: {
    marginEnd: 8,
  },
  headerSection: {
    marginStart: 32,
  },
  headerSectionTitle: {
    fontSize: 11,
    lineHeight: 13,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  headerSectionCount: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  mainSection: {
    flex: 0.5,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite500,
    backgroundColor: Colors.light.hereworksWhite100,
    marginHorizontal: 8,
    padding: 16,
  },
  mainSectionTitle: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    marginBottom: 4,
  },
  mainSectionCount: {
    fontSize: 22,
    lineHeight: 32,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  mainSectionButton: {
    paddingHorizontal: 12,
    paddingVertical: 5,
  },
});

export default SpaceTitleItem;
